import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { Search, UserPlus, UserMinus, Users, AlertTriangle } from "lucide-react";
import { getUsersByRole, updateTeam } from "../../../api/common";
import SubmitButton from "../../../Components/Button";
import Alert from "../../../Components/AlertPopup";
import styles from "./style.module.scss";

interface Member {
  userId: number;
  name: string;
  email: string;
  roleType: string;
}

interface TeamData {
  teamId: number;
  teamName: string;
  leadAuditors: Member[];
  auditors: Member[];
}

interface Props {
  show: boolean;
  onClose: () => void;
  team: TeamData | null;
  onSaveSuccess?: () => void;
}

const ROLE_BADGE_STYLES: Record<string, { border: string; color: string }> = {
  "Lead Auditor": { border: "#102250", color: "#102250" },
  LEAD_AUDITOR: { border: "#102250", color: "#102250" },
  Auditor: { border: "#EA580C", color: "#EA580C" },
  AUDITOR: { border: "#EA580C", color: "#EA580C" },
};

const ROLE_DISPLAY: Record<string, string> = {
  LEAD_AUDITOR: "Lead Auditor",
  AUDITOR: "Auditor",
};

const EditTeamModal: FC<Props> = ({
  show,
  onClose,
  team,
  onSaveSuccess,
}) => {
  const [teamName, setTeamName] = useState("");
  const [currentMembers, setCurrentMembers] = useState<Member[]>([]);
  const [activeTab, setActiveTab] = useState<"current" | "add">("current");
  const [searchTerm, setSearchTerm] = useState("");
  const [allUsers, setAllUsers] = useState<Member[]>([]);
  const [saving, setSaving] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showConfirmRemoval, setShowConfirmRemoval] = useState(false);
  const [membersToRemove, setMembersToRemove] = useState<Member[]>([]);
  const originalMembersRef = useRef<Member[]>([]);

  useEffect(() => {
    if (show && team) {
      setTeamName(team.teamName);
      const members = [
        ...(team.leadAuditors || []),
        ...(team.auditors || []),
      ];
      setCurrentMembers(members);
      originalMembersRef.current = members;
      setActiveTab("current");
      setSearchTerm("");
      setShowSuccessAlert(false);
      setShowErrorAlert(false);
      setShowConfirmRemoval(false);
      fetchAllUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const fetchAllUsers = async () => {
    try {
      const [leadRes, auditorRes] = await Promise.all([
        getUsersByRole("LEAD_AUDITOR"),
        getUsersByRole("AUDITOR"),
      ]);
      const leads = (leadRes && Array.isArray(leadRes) ? leadRes : []).map((u: any) => ({
        ...u,
        roleType: u.roleType || "LEAD_AUDITOR",
      }));
      const auds = (auditorRes && Array.isArray(auditorRes) ? auditorRes : []).map((u: any) => ({
        ...u,
        roleType: u.roleType || "AUDITOR",
      }));
      setAllUsers([...leads, ...auds]);
    } catch {
      // keep empty
    }
  };

  const availableMembers = useMemo(() => {
    return allUsers.filter(
      (m) => !currentMembers.some((cm) => cm.userId === m.userId)
    );
  }, [allUsers, currentMembers]);

  const filteredCurrentMembers = useMemo(() => {
    if (!searchTerm.trim()) return currentMembers;
    const term = searchTerm.toLowerCase();
    return currentMembers.filter((m) => m.name.toLowerCase().includes(term));
  }, [currentMembers, searchTerm]);

  const filteredAvailableMembers = useMemo(() => {
    if (!searchTerm.trim()) return availableMembers;
    const term = searchTerm.toLowerCase();
    return availableMembers.filter((m) => m.name.toLowerCase().includes(term));
  }, [availableMembers, searchTerm]);

  const handleRemoveMember = (userId: number) => {
    setCurrentMembers((prev) => prev.filter((m) => m.userId !== userId));
  };

  const handleAddMember = (member: Member) => {
    setCurrentMembers((prev) => [...prev, member]);
  };

  const handleUpdateClick = () => {
    const removed = originalMembersRef.current.filter(
      (om) => !currentMembers.some((cm) => cm.userId === om.userId)
    );
    if (removed.length > 0) {
      setMembersToRemove(removed);
      setShowConfirmRemoval(true);
    } else {
      handleSave();
    }
  };

  const handleConfirmRemoval = () => {
    setShowConfirmRemoval(false);
    handleSave();
  };

  const handleSave = async () => {
    if (!team) return;
    setSaving(true);
    setShowSuccessAlert(false);
    setShowErrorAlert(false);
    try {
      const leadAuditorIds = currentMembers
        .filter((m) => m.roleType === "LEAD_AUDITOR")
        .map((m) => m.userId);
      const auditorIds = currentMembers
        .filter((m) => m.roleType === "AUDITOR")
        .map((m) => m.userId);

      const payload = {
        teamName,
        leadAuditorIds,
        auditorIds,
      };
      await updateTeam(team.teamId, payload);
      onSaveSuccess?.();
      onClose();
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.response?.data || "Failed to update team. Please try again.";
      setAlertMessage(msg);
      setShowErrorAlert(true);
    } finally {
      setSaving(false);
    }
  };

  const handleSuccessAlertClose = () => {
    setShowSuccessAlert(false);
    onClose();
  };

  const getBadgeStyle = (role: string) => {
    return ROLE_BADGE_STYLES[role] || { border: "#475569", color: "#475569" };
  };

  const getDisplayRole = (roleType: string) => {
    return ROLE_DISPLAY[roleType] || roleType;
  };

  if (!team) return null;

  return (
    <Modal show={show} onHide={onClose} centered size="lg" className={styles.modal}>
      <Modal.Header closeButton>
        <Modal.Title className={styles.modalTitle}>
          <Users size={20} className={styles.titleIcon} />
          Edit Team
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert
          show={showSuccessAlert}
          onClose={handleSuccessAlertClose}
          message={alertMessage}
        />
        <Alert
          type="error"
          show={showErrorAlert}
          onClose={() => setShowErrorAlert(false)}
          message={alertMessage}
        />

        {/* Team Name */}
        <div className={styles.fieldGroup}>
          <label className={styles.fieldLabel}>Team Name</label>
          <Form.Control
            type="text"
            value={teamName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setTeamName(e.target.value)}
            className={styles.teamNameInput}
          />
        </div>

        {/* Manage Team Members */}
        <div className={styles.sectionTitle}>Manage Team Members</div>

        {/* Tabs */}
        <div className={styles.memberTabs}>
          <button
            className={`${styles.memberTab} ${activeTab === "current" ? styles.memberTabActive : ""}`}
            onClick={() => { setActiveTab("current"); setSearchTerm(""); }}
          >
            Current Members ({currentMembers.length})
          </button>
          <button
            className={`${styles.memberTab} ${activeTab === "add" ? styles.memberTabActive : ""}`}
            onClick={() => { setActiveTab("add"); setSearchTerm(""); }}
          >
            Add Members ({availableMembers.length})
          </button>
        </div>

        {/* Search */}
        <div className={styles.searchBox}>
          <Form.Control
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          <Search size={16} className={styles.searchIcon} />
        </div>

        {/* Member List */}
        <div className={styles.memberList}>
          {activeTab === "current" ? (
            filteredCurrentMembers.length > 0 ? (
              filteredCurrentMembers.map((member) => {
                const badge = getBadgeStyle(member.roleType);
                return (
                  <div key={member.userId} className={styles.memberRow}>
                    <div className={styles.memberInfo}>
                      <span className={styles.memberName}>{member.name}</span>
                      <span
                        className={styles.roleBadge}
                        style={{ borderColor: badge.border, color: badge.color }}
                      >
                        {getDisplayRole(member.roleType)}
                      </span>
                    </div>
                    <button
                      className={styles.removeBtn}
                      onClick={() => handleRemoveMember(member.userId)}
                    >
                      <UserMinus size={16} />
                    </button>
                  </div>
                );
              })
            ) : (
              <div className={styles.noMembers}>No members found</div>
            )
          ) : (
            filteredAvailableMembers.length > 0 ? (
              filteredAvailableMembers.map((member) => {
                const badge = getBadgeStyle(member.roleType);
                return (
                  <div key={member.userId} className={styles.memberRow}>
                    <div className={styles.memberInfo}>
                      <span className={styles.memberName}>{member.name}</span>
                      <span
                        className={styles.roleBadge}
                        style={{ borderColor: badge.border, color: badge.color }}
                      >
                        {getDisplayRole(member.roleType)}
                      </span>
                    </div>
                    <button
                      className={styles.addBtn}
                      onClick={() => handleAddMember(member)}
                    >
                      <UserPlus size={16} />
                    </button>
                  </div>
                );
              })
            ) : (
              <div className={styles.noMembers}>No members available</div>
            )
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <SubmitButton type="secondary" onClick={onClose} className={styles.cancelBtn}>
          Cancel
        </SubmitButton>
        <SubmitButton onClick={handleUpdateClick} className={styles.saveBtn} disabled={saving}>
          {saving ? "Updating..." : "Update"}
        </SubmitButton>
      </Modal.Footer>

      {/* Confirmation Popup for Member Removal */}
      <Modal
        show={showConfirmRemoval}
        onHide={() => setShowConfirmRemoval(false)}
        centered
        className={styles.confirmModal}
      >
        <Modal.Header closeButton>
          <Modal.Title className={styles.confirmTitle}>
            <AlertTriangle size={20} className={styles.confirmIcon} />
            Confirm Member Removal
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className={styles.confirmMessage}>
            The following members will be removed from the team:
          </p>
          <ul className={styles.removedMemberList}>
            {membersToRemove.map((member) => (
              <li key={member.userId} className={styles.removedMemberItem}>
                <span className={styles.removedMemberName}>{member.name}</span>
                <span
                  className={styles.roleBadge}
                  style={{
                    borderColor: getBadgeStyle(member.roleType).border,
                    color: getBadgeStyle(member.roleType).color,
                  }}
                >
                  {getDisplayRole(member.roleType)}
                </span>
              </li>
            ))}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          <SubmitButton type="secondary" onClick={() => setShowConfirmRemoval(false)} 
          className={styles.cancelBtn}
          >
            Cancel
          </SubmitButton>
          <SubmitButton onClick={handleConfirmRemoval} className={styles.confirmBtn}>
            Confirm
          </SubmitButton>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default EditTeamModal;